import { Box, SimpleGrid, Text, Badge, Group, Skeleton } from '@mantine/core'
import { AreaChart } from '@mantine/charts'
import { IconChartArea, IconActivity } from '@tabler/icons-react'
import { WidgetCard, CardHeader, EmptyState } from '@/components/ui'

interface PerformanceChartsProps {
  cwMetrics: any
  loading?: boolean
}

export function PerformanceCharts({ cwMetrics, loading }: PerformanceChartsProps) {
  const ec2Datapoints: number[] = cwMetrics?.ec2?.datapoints ?? []
  const albDatapoints: number[] = cwMetrics?.alb?.datapoints ?? []

  const ec2Data = ec2Datapoints.map((v, i) => ({ hour: `${i}h`, cpu: parseFloat(v.toFixed(1)) }))
  const albData = albDatapoints.map((v, i) => ({ hour: `${i}h`, latency: parseFloat(v.toFixed(1)) }))

  const cpuAvg = ec2Datapoints.length > 0
    ? (ec2Datapoints.reduce((a, b) => a + b, 0) / ec2Datapoints.length).toFixed(1)
    : null

  const latencyP95 = albDatapoints.length > 0
    ? albDatapoints.slice().sort((a, b) => a - b)[Math.floor(albDatapoints.length * 0.95)].toFixed(0)
    : null

  return (
    <SimpleGrid cols={{ base: 1, md: 2 }} spacing="md">
      {/* EC2 Fleet — CPU & Memoria */}
      <WidgetCard accent="success" p={0}>
        <CardHeader
          title="EC2 Fleet — CPU & Memória (24h)"
          badge={cpuAvg !== null ? { value: `avg ${cpuAvg}%`, color: 'green' } : undefined}
        />
        <Box p="md">
          {loading ? (
            <Skeleton height={160} />
          ) : ec2Data.length === 0 ? (
            <EmptyState
              bare
              size="sm"
              accent="success"
              icon={IconChartArea}
              title="Sem métricas disponíveis"
              description="Aguardando datapoints do CloudWatch para a frota EC2."
            />
          ) : (
            <>
              <AreaChart
                h={160}
                data={ec2Data}
                dataKey="hour"
                series={[
                  { name: 'cpu', color: 'teal.6', label: 'CPU %' },
                ]}
                curveType="monotone"
                withDots={false}
                withGradient
                fillOpacity={0.15}
                withXAxis
                withYAxis={false}
                withTooltip
                withLegend
              />
              <Group gap="xs" mt="xs">
                <Badge size="xs" color="teal" variant="light">CPU</Badge>
                {cpuAvg !== null && (
                  <Text size="xs" c="dimmed">Média 24h: <Text component="span" fw={600} c="teal">{cpuAvg}%</Text></Text>
                )}
              </Group>
            </>
          )}
        </Box>
      </WidgetCard>

      {/* ALB — Latencia & Requests */}
      <WidgetCard accent="info" p={0}>
        <CardHeader
          title="ALB — Latência & Requests (24h)"
          badge={latencyP95 !== null ? { value: `p95 ${latencyP95}ms`, color: 'blue' } : undefined}
        />
        <Box p="md">
          {loading ? (
            <Skeleton height={160} />
          ) : albData.length === 0 ? (
            <EmptyState
              bare
              size="sm"
              accent="info"
              icon={IconActivity}
              title="Sem métricas disponíveis"
              description="Aguardando datapoints do CloudWatch para o ALB."
            />
          ) : (
            <>
              <AreaChart
                h={160}
                data={albData}
                dataKey="hour"
                series={[
                  { name: 'latency', color: 'blue.6', label: 'Latência (ms)' },
                ]}
                curveType="monotone"
                withDots={false}
                withGradient
                fillOpacity={0.15}
                withXAxis
                withYAxis={false}
                withTooltip
                withLegend
              />
              <Group gap="xs" mt="xs">
                <Badge size="xs" color="blue" variant="light">Latência</Badge>
                {latencyP95 !== null && (
                  <Text size="xs" c="dimmed">p95 24h: <Text component="span" fw={600} c="blue">{latencyP95}ms</Text></Text>
                )}
              </Group>
            </>
          )}
        </Box>
      </WidgetCard>
    </SimpleGrid>
  )
}
